import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Users, Calendar } from 'lucide-react';

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Ordered newest → oldest (by year). "Early Years" placed last.
  const galleryImages = [
    {
      id: 1,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/mwespm3g_WhatsApp%20Image%202026-04-29%20at%205.58.38%20PM.jpeg",
      title: "RST & Co. Team 2026",
      year: "2026",
      description: "Our growing family - celebrating success together",
      category: "Team"
    },
    {
      id: 2,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/4ysvdzcm_WhatsApp%20Image%202026-04-29%20at%206.06.27%20PM.jpeg",
      title: "Yellow Day Celebration",
      year: "2025",
      description: "Team spirit shining bright in coordinated yellow",
      category: "Celebration"
    },
    {
      id: 3,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/40uvlukd_WhatsApp%20Image%202026-04-29%20at%206.06.26%20PM.jpeg",
      title: "Red Day Team Gathering",
      year: "2024",
      description: "Unity and passion reflected in our red theme celebration",
      category: "Celebration"
    },
    {
      id: 4,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/5qbnszwq_WhatsApp%20Image%202026-04-29%20at%206.06.26%20PM%20%281%29.jpeg",
      title: "Casual Team Day",
      year: "2024",
      description: "Building bonds in a relaxed, friendly environment",
      category: "Team Culture"
    },
    {
      id: 8,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/sk1vdbs2_WhatsApp%20Image%202026-04-29%20at%205.55.04%20PM.jpeg",
      title: "Team Outing",
      year: "2024",
      description: "Building bonds beyond the office",
      category: "Team Building"
    },
    {
      id: 10,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/qh3z6dcg_WhatsApp%20Image%202026-04-29%20at%205.58.38%20PM.jpeg",
      title: "Growing Together",
      year: "2023",
      description: "Strength in unity, excellence in diversity",
      category: "Team"
    },
    {
      id: 7,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/hcykyo7h_WhatsApp%20Image%202026-04-29%20at%205.53.46%20PM.jpeg",
      title: "Office Celebrations",
      year: "2018",
      description: "Creating memorable moments in our workspace",
      category: "Culture"
    },
    {
      id: 9,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/l2y7satc_WhatsApp%20Image%202026-04-29%20at%206.00.24%20PM.jpeg",
      title: "Office Moments",
      year: "2016",
      description: "Everyday excellence in our professional workspace",
      category: "Work Life"
    },
    {
      id: 6,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/ilfud8n0_WhatsApp%20Image%202026-04-29%20at%205.56.14%20PM.jpeg",
      title: "Team Milestones",
      year: "2015",
      description: "Celebrating achievements and growth milestones",
      category: "Celebration"
    },
    {
      id: 5,
      url: "https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/twflvzdv_WhatsApp%20Image%202026-04-29%20at%205.51.28%20PM.jpeg",
      title: "Leadership & Team",
      year: "Early Years",
      description: "Building foundations with dedication and vision",
      category: "Leadership"
    }
  ];

  const openLightbox = (index) => {
    setSelectedImage(galleryImages[index]);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  return (
    <section id="gallery" className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <div className="inline-block bg-brand-blue/10 px-4 py-1.5 rounded-md mb-6">
            <span className="font-mono text-xs tracking-widest text-brand-blue uppercase">Our Journey</span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
            15+ Years of
            <br />
            <span className="text-brand-blue">Excellence & Growth</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From humble beginnings to a thriving team, witness our journey of dedication, teamwork, and continuous growth in serving our clients.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleryImages.map((image, index) => (
            <div
              key={image.id}
              className={`group relative overflow-hidden cursor-pointer bg-gray-200 transition-all duration-500 hover:shadow-2xl ${
                index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
              }`}
              onClick={() => openLightbox(index)}
              style={{ aspectRatio: index === 0 ? '16/9' : '4/3' }}
            >
              {/* Image */}
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  {/* Year Badge */}
                  <div className="inline-flex items-center gap-2 bg-brand-blue/20 backdrop-blur-sm border border-white/20 px-3 py-1 rounded-full mb-3">
                    <Calendar size={14} strokeWidth={2} />
                    <span className="text-xs font-medium">{image.year}</span>
                  </div>

                  {/* Title */}
                  <h3 className="font-heading text-xl md:text-2xl font-bold mb-2">
                    {image.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-200 mb-3">
                    {image.description}
                  </p>

                  {/* Category Tag */}
                  <div className="flex items-center gap-2 text-xs">
                    <Users size={14} strokeWidth={2} />
                    <span className="font-medium">{image.category}</span>
                  </div>
                </div>
              </div>

              {/* Zoom Indicator */}
              <div className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-gray-900 text-lg">+</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Text */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            Every photo tells a story of commitment, collaboration, and the strong bonds we've built over the years. Together, we continue to grow stronger.
          </p>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X className="text-white" size={24} strokeWidth={2} />
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-colors z-10"
            aria-label="Previous image"
          >
            <ChevronLeft className="text-white" size={24} strokeWidth={2} />
          </button>

          {/* Next Button */}
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-colors z-10"
            aria-label="Next image"
          >
            <ChevronRight className="text-white" size={24} strokeWidth={2} />
          </button>

          {/* Image Container */}
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[85vh] object-contain"
            />

            {/* Image Info */}
            <div className="mt-6 text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-3">
                <Calendar className="text-brand-blue" size={16} strokeWidth={2} />
                <span className="text-white text-sm font-medium">{selectedImage.year}</span>
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                {selectedImage.title}
              </h3>
              <p className="text-gray-300 text-sm md:text-base">
                {selectedImage.description}
              </p>
              <p className="text-gray-400 text-xs mt-2">
                {currentIndex + 1} / {galleryImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
